/**
 * @fileoverview Input/output schemas for the draft-judge agent.
 *
 * This agent reviews drafting history to make a satisfaction decision.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { DraftingHistorySchema, CritiqueDataSchema } from '@/domains/chapter-workspace/schemas/chapter';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.styleGuide.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema)
  .extend({
    draftingHistory: DraftingHistorySchema.describe(
      'The complete drafting history including all content versions, critiques, and evaluations'
    ),
    critiqueData: CritiqueDataSchema.optional().describe(
      'Persisted critique data including all findings from review agents'
    ),
    storyPov: z.string().optional().describe(
      'The story-level POV style (e.g., "First Person", "Third Person Limited")'
    ),
    chapterPovCharacter: z.string().optional().describe(
      'The POV character for the current chapter, or "Third Person Omniscient"'
    ),
    qualityMetrics: z.object({
      majorFindingsCount: z.number().describe('Number of major severity findings'),
      criticalFindingsCount: z.number().describe('Number of critical severity findings'),
      averageRating: z.number().nullable().describe('Average rating across all review agents'),
      totalRatingsCount: z.number().describe('Number of ratings used in average'),
    }).optional().describe('Pre-calculated quality metrics from persisted data'),
  });

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Unified output schema for the draft judge agent.
 */
export const OutputSchema = z.object({
  commentary: z.string().describe(
    'Brief reasoning about the quality judgment (2-3 sentences). ' +
    'This will be recorded in the drafting history.'
  ),
  satisfied: z.boolean().describe(
    'Whether the draft meets the quality threshold and iteration should stop'
  ),
  topPriorities: z.array(z.string()).max(3).optional().describe(
    'If not satisfied, the 1-3 most important issues to address in the next iteration'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
